import { UTCDate } from '@date-fns/utc';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTimePicker } from '@optools/components/datetime/date-time-picker';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { Checkbox } from '@optools/components/ui/checkbox';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { Label } from '@optools/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Switch } from '@optools/components/ui/switch';
import {
  MilestoneInput,
  MilestoneSchema,
  MilestoneType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { add } from 'date-fns';
import { CircleHelp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { TimeCodePicker } from './time-code-picker';

const formSchema = z.object({
  absoluteTime: z.iso.datetime({ offset: true }),
  inning: z.coerce.number<number>().min(1, { error: 'Inning number must be greater than 0' }),
  isGameOver: z.boolean(),
  isTopInning: z.boolean(),
  milestoneType: z.any(),
  videoTimestamp: z.string(),
});

export type MilestoneFormValues = Pick<
  MilestoneInput,
  'absoluteTime' | 'keywords' | 'milestoneType'
>;

type MilestoneFormSchema = z.infer<typeof formSchema>;

export interface MilestoneFormProps {
  defaultValues?: Partial<MilestoneFormSchema>;
  milestoneSchemas?: MilestoneSchema[];
  onCancel?: () => void;
  onSubmit: (values: MilestoneFormValues) => void;
  programDateTime: string | undefined;
}

export function MilestoneForm({
  defaultValues,
  milestoneSchemas,
  onCancel,
  onSubmit,
  programDateTime,
}: MilestoneFormProps) {
  const [useDate, setUseDate] = useState(false);

  const form = useForm<MilestoneFormSchema>({
    defaultValues: {
      absoluteTime: programDateTime
        ? new UTCDate(programDateTime).toISOString()
        : new UTCDate().toISOString(),
      inning: 1,
      isGameOver: false,
      isTopInning: false,
      milestoneType: MilestoneType.INNING_START,
      videoTimestamp: '00:00:00',
      ...defaultValues,
    },
    resolver: zodResolver(formSchema),
  });

  const milestoneType = form.watch('milestoneType');

  const selectedMilestoneSchema = useMemo(
    () => milestoneSchemas?.find((item) => item.name === milestoneType),
    [milestoneType, milestoneSchemas],
  );

  const handleSubmit = (values: MilestoneFormSchema) => {
    const { absoluteTime, videoTimestamp, ...otherValues } = values;

    let milestoneTime;
    if (useDate) {
      // Set the milestone time to the selected date time.
      milestoneTime = absoluteTime;
    } else if (programDateTime) {
      // Add field values to program date start to determine the milestone absolute time.
      const [hours, minutes, seconds] = videoTimestamp.split(':').map(Number);
      milestoneTime = add(programDateTime, {
        hours,
        minutes,
        seconds,
      }).toISOString();
    }

    const optionalFields = selectedMilestoneSchema?.metadataFields.map((field) => ({
      name: field.name,
      value: (otherValues as any)[field.name],
    }));

    if (!milestoneTime) {
      toast.error('Submission failed, missing program date time', { duration: Infinity });
      return;
    }

    onSubmit({
      absoluteTime: milestoneTime,
      keywords: optionalFields,
      milestoneType: values.milestoneType,
    });
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onReset={() => form.reset()}
        onSubmit={form.handleSubmit(handleSubmit)}>
        {milestoneSchemas?.length && (
          <FormField
            control={form.control}
            name="milestoneType"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="milestone-type-combobox">Milestone Type</FormLabel>
                <FormControl>
                  <Select
                    defaultValue={field.value}
                    value={field.value}
                    onValueChange={field.onChange}>
                    <SelectTrigger id="milestone-type-combobox">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {milestoneSchemas.map((item) => (
                        <SelectItem key={item.name} value={item.name}>
                          <div>{item.name}</div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className="flex flex-col">
          <FormLabel className="mb-2 text-sm">Milestone Time</FormLabel>
          <Card className="flex flex-col gap-4 p-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm" htmlFor="use-date-time-switch">
                Timestamp
              </Label>
              <Switch
                checked={useDate}
                id="use-date-time-switch"
                size="sm"
                onCheckedChange={setUseDate}
              />
              <Label className="text-sm" htmlFor="use-date-time-switch">
                Date Time
              </Label>
              <Popover>
                <PopoverTrigger>
                  <CircleHelp className="size-4" role="presentation" />
                </PopoverTrigger>
                <PopoverContent align="start" className="sm:w-full sm:max-w-96" side="right">
                  <ul className="space-y-4">
                    <li>
                      <strong className="flex items-center gap-1 text-sm">Timestamp</strong>
                      <span className="text-xs">
                        The video playback position at which this milestone occurs
                      </span>
                    </li>
                    <li>
                      <strong className="flex items-center gap-1 text-sm">Date Time</strong>
                      <span className="text-xs">
                        The local date time at which this milestone occurs
                      </span>
                    </li>
                  </ul>
                </PopoverContent>
              </Popover>
            </div>

            {!useDate && (
              <FormField
                control={form.control}
                name="videoTimestamp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video Timestamp</FormLabel>
                    <FormControl>
                      <TimeCodePicker setTimecode={field.onChange} timecode={field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {useDate && (
              <FormField
                control={form.control}
                name="absoluteTime"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {milestoneType === MilestoneType.STREAM_START
                        ? 'Stream Start Time (UTC)'
                        : 'Milestone Start Time (UTC)'}
                    </FormLabel>
                    <FormControl>
                      <DateTimePicker
                        date={new UTCDate(field.value)}
                        setDate={(date) => field.onChange(date.toISOString())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </Card>
        </div>
        {selectedMilestoneSchema?.metadataFields?.find((item) => item.name === 'inning') && (
          <FormField
            control={form.control}
            name="inning"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Inning</FormLabel>
                <FormControl>
                  <Input className="sm:w-1/6" type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {selectedMilestoneSchema?.metadataFields?.find((item) => item.name === 'isTopInning') && (
          <FormField
            control={form.control}
            name="isTopInning"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-start gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      data-testid="isTopInning"
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer">Top of Inning</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {selectedMilestoneSchema?.metadataFields?.find((item) => item.name === 'isGameOver') && (
          <FormField
            control={form.control}
            name="isGameOver"
            render={({ field }) => (
              <FormItem className="flex items-center justify-start gap-2">
                <div className="flex items-center justify-start gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      data-testid="isGameOver"
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer">Game Over</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <DialogFooter>
          <Button
            type="reset"
            variant="secondary"
            onClick={() => {
              onCancel?.();
              form.reset();
            }}>
            Cancel
          </Button>
          <Button type="submit">Submit</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
